/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { SterilityTestServices } from '../../../service/sterilityTestServices';

interface SterilityTestDetailsProps {
  sterilityTestId: string;
  onBack: () => void;
  onRecord?: () => void;
  onViewObservation?: () => void;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// "2026-10-07 15:00:55" -> "07-Oct-2026 15:00"
const formatDateTime = (value?: string | null): string => {
  if (!value) return '-';
  const [datePart, timePart] = value.split(' ');
  const [y, m, d] = (datePart || '').split('-');
  if (!y || !m || !d) return value;
  return `${d}-${MONTHS[Number(m) - 1] ?? m}-${y}${timePart ? ' ' + timePart.slice(0, 5) : ''}`;
};

// "IN_PROGRESS" -> "In Progress"
const formatStatus = (value?: string | null): string => {
  if (!value) return '-';
  return value
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

const SterilityTestDetails: React.FC<SterilityTestDetailsProps> = ({ sterilityTestId, onBack, onRecord, onViewObservation }) => {
  const [activeTab, setActiveTab] = useState<'Summary' | 'Tubes' | 'Observations' | 'Timeline' | 'Audit'>('Summary');
  const [viewData, setViewData] = useState<any>(null);

  const details = viewData?.test_details;
  const incubation = viewData?.incubation_details;
  const tubes: any[] = viewData?.test_tube_details ?? [];
  const observationHistory: any[] = viewData?.observation_history ?? [];

  // Media tube search: text typed in the box is only applied when Filter is clicked (or Enter is pressed)
  const [tubeSearchInput, setTubeSearchInput] = useState('');
  const [appliedTubeSearch, setAppliedTubeSearch] = useState('');

  const applyTubeFilter = () => setAppliedTubeSearch(tubeSearchInput.trim());

  const handleTubeSearchChange = (value: string) => {
    setTubeSearchInput(value);
    // clearing the box restores the full list
    if (value.trim() === '') setAppliedTubeSearch('');
  };

  const filteredTubes = tubes.filter((t: any) => {
    const q = appliedTubeSearch.toLowerCase();
    if (!q) return true;
    return [t.tube_id, t.serial_number, t.media, t.lot_number, t.batch_number]
      .some((v) => String(v ?? '').toLowerCase().includes(q));
  });

  const testHeader = {
    id: details?.sterility_test_identifier ?? '-',
    productName: details?.product_name ?? '-',
    batchNumber: details?.media_tube_batch ?? '-',
    method: details?.test_method ?? '-',
    status: formatStatus(details?.activity_status),
    startDate: formatDateTime(details?.initiated_date_time)
  };

  // Media utilization: group tubes by media + lot
  const mediaGroups = Object.values(
    tubes.reduce((acc: Record<string, { media: string; lot: string; count: number }>, t: any) => {
      const media = t.media ?? '-';
      const lot = t.lot_number ?? '-';
      const key = `${media}|${lot}`;
      if (!acc[key]) acc[key] = { media, lot, count: 0 };
      acc[key].count += 1;
      return acc;
    }, {})
  );

  // An observation is "recorded" only once the user has actually captured it.
  // NOTE: `date_time` / `scheduled_date_time` are the planned slot, so they must not be used to detect completion.
  const isRecorded = (o: any): boolean => !!(o.observation_date_time || o.observation);

  // Observation schedule
  const scheduleRows = observationHistory.map((o: any) => ({
    day: `Day ${o.day}`,
    planned: formatDateTime(o.scheduled_date_time ?? o.date_time),
    actual: formatDateTime(o.observation_date_time),
    status: isRecorded(o) ? 'Completed' : 'Pending',
    user: o.by ?? '-'
  }));

  // Recorded observations (only entries that have actually been recorded)
  // `tube_id` in observation_history is the tube serial number (e.g. "ZENT-TT28"),
  // so it is matched against `serial_number` of test_tube_details (whose `tube_id` is numeric).
  const recordedRows = observationHistory
    .filter(isRecorded)
    .map((o: any) => ({
      date: formatDateTime(o.observation_date_time),
      media: tubes.find((t: any) => t.serial_number === o.tube_id)?.media ?? '-',
      clarity: o.visual_clarity_assessment ?? '-',
      growth: o.observation ?? '-',
      status: 'Recorded',
      remarks: o.remarks ?? '-'
    }));

  const timelineSteps = [
    { label: 'Initiated', status: 'completed', date: '12-Aug 10:45' },
    { label: 'Incubation Started', status: 'completed', date: '12-Aug 14:00' },
    { label: 'Day 3 Observation', status: 'active', date: '15-Aug (Due)' },
    { label: 'Day 7 Observation', status: 'pending', date: '19-Aug' },
    { label: 'Day 14 Observation', status: 'pending', date: '26-Aug' },
    { label: 'Completed', status: 'pending', date: '-' }
  ];

   const handleViewDetails = async (testId: string) => {
      const response = await SterilityTestServices.getSterilityTestLabApprovalView(testId);
      if (response?.status && response?.data) {
         setViewData(response.data);
      }
   };

   useEffect(() => {
      handleViewDetails(sterilityTestId)
   }, [sterilityTestId])

  return (
    <div className="min-h-screen bg-[#F8FAFC] p-8 pb-20 media-prep-scope">
      {/* Header Section */}
      <div className="flex flex-col lg:flex-row justify-between gap-8 my-2">
        <div className="space-y-4">
          <div className="flex items-center gap-3">
             <button 
                onClick={onBack}
                className="w-10 h-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center text-slate-400 hover:text-[#0F172A] hover:bg-slate-50 transition-all shadow-sm"
             >
                <span className="material-symbols-outlined text-xl">arrow_back</span>
             </button>
             <div>
                <h1 className="text-2xl font-black text-slate-800 tracking-tight uppercase">{testHeader.id}</h1>
                <p className="text-slate-500 font-medium text-sm flex items-center gap-2">
                  <span className="material-symbols-outlined text-xs">vaccines</span>
                  {testHeader.productName} • {testHeader.batchNumber}
                </p>
             </div>
          </div>
          
          <div className="flex flex-wrap gap-6 pl-13">
             <div className="bg-white px-4 py-2 rounded-xl border border-slate-200 shadow-sm">
                <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Method</p>
                <p className="text-xs font-bold text-slate-700">{testHeader.method}</p>
             </div>
             <div className="bg-white px-4 py-2 rounded-xl border border-slate-200 shadow-sm">
                <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Start Date</p>
                <p className="text-xs font-bold text-slate-700">{testHeader.startDate}</p>
             </div>
             <div className="bg-emerald-50 px-4 py-2 rounded-xl border border-emerald-100 shadow-sm">
                <p className="text-[8px] font-black text-emerald-500 uppercase tracking-widest">Status</p>
                <p className="text-xs font-black text-emerald-600">{testHeader.status}</p>
             </div>
          </div>
        </div>

        {/* <div className="flex flex-col sm:flex-row gap-4 max-h-[40px]">
           <button 
             onClick={onRecord}
             className="flex items-center justify-center gap-2 px-6 py-3 bg-[#FF7043] text-white rounded-2xl text-[11px] font-black uppercase tracking-widest hover:brightness-110 transition-all shadow-sm shadow-orange-500/20 active:scale-95"
           >
              <span className="material-symbols-outlined text-base">edit_document</span>
              Record Observation
           </button>
           <button className="flex items-center justify-center gap-2 px-6 py-3 bg-white border border-slate-200 text-slate-600 rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-slate-50 transition-all shadow-sm">
              <span className="material-symbols-outlined text-base">download</span>
              Download Report
           </button>
           <button className="flex items-center justify-center gap-2 px-6 py-3 bg-white border border-slate-200 text-slate-600 rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-slate-50 transition-all shadow-sm">
              <span className="material-symbols-outlined text-base">print</span>
              Print
           </button>
        </div> */}
      </div>

      <div className="grid grid-cols-12 gap-8 mb-2 mt-4">
        <div className="col-span-12 lg:col-span-8 space-y-8">
          {/* Custom Tabs */}
          <div className="flex gap-1 p-1.5 bg-slate-200/50 rounded-2xl w-fit">
            {(['Summary', 'Tubes', 'Observations'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-8 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                  activeTab === tab 
                  ? 'bg-white text-[#FF7043] shadow-sm' 
                  : 'text-slate-500 hover:text-[#FF7043]'
                }`}
              >
                {tab === 'Summary' ? 'Test Summary' : tab === 'Tubes' ? 'Media Tube Details' : tab === 'Observations' ? 'Observation History' : tab === 'Timeline' ? 'Test Timeline' : 'Audit Trail'}
              </button>
            ))}
          </div>

          <div className="min-h-[600px]">
            <AnimatePresence mode="wait">
              {activeTab === 'Summary' && (
                <motion.div 
                  key="summary"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="space-y-8"
                >
                  <div className="grid grid-cols-2 gap-8">
                    {/* Test Configuration */}
                    <div className="bg-white rounded-[2.5rem] border border-slate-100 p-8 shadow-sm space-y-8">
                      <div className="flex items-center gap-3 border-b border-slate-50 pb-6">
                        <span className="material-symbols-outlined text-[#FF7043]">settings</span>
                        <h3 className="text-xs font-black text-slate-800 uppercase tracking-widest">Test Configuration</h3>
                      </div>
                      <div className="grid grid-cols-2 gap-y-6">
                        <div>
                          <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Test Method</p>
                          <p className="text-xs font-bold text-slate-700 mt-1">{testHeader.method}</p>
                        </div>
                        <div>
                          <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Test Duration</p>
                          <p className="text-xs font-bold text-slate-700 mt-1">{incubation?.duration != null ? `${incubation.duration} Days` : '-'}</p>
                        </div>
                        <div>
                          <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Incubator</p>
                          <p className="text-xs font-bold text-slate-700 mt-1">{incubation?.incubator_id ?? '-'}</p>
                        </div>
                        <div>
                          <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Temp Range</p>
                          <p className="text-xs font-bold text-slate-700 mt-1">{incubation?.temperature ?? '-'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Media Utilization */}
                    <div className="bg-white rounded-[2.5rem] border border-slate-100 p-8 shadow-sm space-y-8">
                       <div className="flex items-center gap-3 border-b border-slate-50 pb-6">
                        <span className="material-symbols-outlined text-blue-500">inventory_2</span>
                        <h3 className="text-xs font-black text-slate-800 uppercase tracking-widest">Media Utilization</h3>
                      </div>
                      <div className="space-y-6">
                        {mediaGroups.map((g: any) => (
                          <div key={`${g.media}-${g.lot}`} className="flex items-center justify-between">
                            <div>
                              <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest">{g.media}</p>
                              <p className="text-xs font-bold text-slate-700 mt-0.5">Lot: {g.lot}</p>
                            </div>
                            <div className="text-right">
                               <p className="text-xs font-black text-[#0F172A]">{g.count} {g.count === 1 ? 'Tube' : 'Tubes'}</p>
                               <p className="text-[8px] font-black text-emerald-500 uppercase tracking-widest">{incubation?.summary ?? '-'}</p>
                            </div>
                          </div>
                        ))}
                        {mediaGroups.length === 0 && (
                          <p className="text-xs font-medium text-slate-400 italic">No media tubes assigned</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Progress Tracker */}
                  {/* <div className="bg-white rounded-[2.5rem] border border-slate-100 p-8 shadow-sm space-y-10">
                    <div className="flex items-center gap-3">
                      <span className="material-symbols-outlined text-purple-500">query_stats</span>
                      <h3 className="text-xs font-black text-slate-800 uppercase tracking-widest">Progress Tracker</h3>
                    </div>

                    <div className="relative pt-2 pb-8">
                      <div className="absolute top-6 left-0 w-full h-1 bg-slate-100 -translate-y-1/2 rounded-full overflow-hidden">
                         <div className="h-full bg-orange-500" style={{ width: '45%' }}></div>
                      </div>
                      
                      <div className="relative flex justify-between">
                         {timelineSteps.map((step, idx) => (
                           <div key={idx} className="flex flex-col items-center gap-4 relative z-10 w-32">
                              <div className={`w-12 h-12 rounded-full flex items-center justify-center border-4 border-white shadow-xl transition-all ${
                                step.status === 'completed' ? 'bg-orange-500 text-white' :
                                step.status === 'active' ? 'bg-white text-orange-500 ring-8 ring-orange-100' :
                                'bg-slate-100 text-slate-400'
                              }`}>
                                 {step.status === 'completed' ? (
                                   <span className="material-symbols-outlined text-lg font-black">check</span>
                                 ) : (
                                   <div className={`w-3 h-3 rounded-full ${step.status === 'active' ? 'bg-orange-500 animate-pulse' : 'bg-slate-400'}`} />
                                 )}
                              </div>
                              <div className="text-center px-2">
                                <p className={`text-[10px] font-black uppercase tracking-widest ${
                                  step.status === 'pending' ? 'text-slate-400' : 
                                  step.status === 'active' ? 'text-orange-600' : 
                                  'text-slate-800'
                                }`}>{step.label}</p>
                                <p className="text-[9px] font-black text-slate-400 mt-1 uppercase tracking-tight opacity-80">{step.date}</p>
                              </div>
                           </div>
                         ))}
                      </div>
                    </div>
                  </div> */}
                </motion.div>
              )}

              {activeTab === 'Tubes' && (
                <motion.div 
                  key="tubes"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden text-black"
                >
                   <div className="px-8 py-6 border-b border-slate-50 flex items-center justify-between bg-slate-50/50">
                      <div className="flex items-center gap-4">
                        <div className="relative w-64">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400 text-lg">search</span>
                          <input 
                            type="text" 
                            value={tubeSearchInput}
                            onChange={(e) => handleTubeSearchChange(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') applyTubeFilter(); }}
                            placeholder="Search Tube ID..."
                            className="w-full bg-white border border-slate-200 rounded-xl py-2 pl-10 pr-4 text-xs font-medium outline-none focus:border-blue-500 transition-all"
                          />
                        </div>
                        <div className="flex gap-2">
                           <button
                             onClick={applyTubeFilter}
                             className="px-4 py-2 rounded-xl bg-white border border-slate-200 text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-slate-800 transition-all"
                           >
                             Filter
                           </button>
                        </div>
                      </div>
                   </div>
                   <table className="w-full border-collapse">
                      <thead>
                         <tr className="bg-slate-50/20">
                            <th className="px-8 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">Tube ID</th>
                            <th className="px-8 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">QR Code</th>
                            <th className="px-8 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">Media Type</th>
                            <th className="px-8 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">Batch</th>
                            <th className="px-8 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">Lot #</th>
                            <th className="px-8 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">Status</th>
                         </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-50">
                         {filteredTubes.length === 0 && (
                           <tr>
                             <td colSpan={6} className="px-8 py-6 text-center text-[11px] font-medium text-slate-400 italic">
                               {tubes.length === 0 ? 'No media tubes assigned' : 'No tubes match the search or filter'}
                             </td>
                           </tr>
                         )}
                         {filteredTubes.map((t: any) => (
                           <tr key={t.tube_id} className="hover:bg-slate-50/30 transition-colors">
                              <td className="px-8 py-4">
                                <span className="text-xs font-black text-slate-700">{t.tube_id ?? '-'}</span>
                              </td>
                              <td className="px-8 py-4 font-mono text-[10px] text-blue-600 font-bold">{t.serial_number ?? '-'}</td>
                              <td className="px-8 py-4 text-xs font-bold text-slate-500">{t.media ?? '-'}</td>
                              <td className="px-8 py-4 text-xs font-bold text-slate-600 tracking-tight">{t.batch_number ?? details?.media_tube_batch ?? '-'}</td>
                              <td className="px-8 py-4 text-xs font-medium text-slate-500">{t.lot_number ?? '-'}</td>
                              <td className="px-8 py-4">
                                <span className="px-3 py-1 bg-emerald-50 text-emerald-600 border border-emerald-100 rounded-full text-[9px] font-black uppercase tracking-widest">{formatStatus(t.tube_status)}</span>
                              </td>
                           </tr>
                         ))}
                      </tbody>
                   </table>
                </motion.div>
              )}

              {activeTab === 'Observations' && (
                <motion.div 
                  key="observations"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="space-y-8"
                >
                   <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden mt-8">
                      <div className="px-8 py-6 border-b border-slate-50 bg-slate-50/30">
                         <h3 className="text-xs font-black text-slate-800 uppercase tracking-widest flex items-center gap-2">
                            <span className="material-symbols-outlined text-base text-orange-500">calendar_month</span>
                            Observation Schedule
                         </h3>
                      </div>
                      <div className="overflow-x-auto h-[60vh]">
                         <table className="w-full">
                            <thead className="bg-slate-50/50">
                            <tr>
                               <th className="px-8 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest">Day</th>
                               <th className="px-8 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest">Planned Date</th>
                               <th className="px-8 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest">Actual Date</th>
                               <th className="px-8 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest">Status</th>
                               <th className="px-8 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest">Recorded By</th>
                            </tr>
                         </thead>
                         <tbody className="divide-y divide-slate-50">
                            {scheduleRows.map((o, idx) => (
                              <tr key={idx}>
                                 <td className="px-8 py-4 text-[11px] font-black text-slate-700">{o.day}</td>
                                 <td className="px-8 py-4 text-[11px] font-medium text-slate-500">{o.planned}</td>
                                 <td className="px-8 py-4 text-[11px] font-medium text-slate-500">{o.actual}</td>
                                 <td className="px-8 py-4">
                                    <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest ${
                                      o.status === 'Completed' ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-400'
                                    }`}>{o.status}</span>
                                 </td>
                                 <td className="px-8 py-4 text-[11px] font-bold text-slate-600 italic">{o.user}</td>
                              </tr>
                            ))}
                         </tbody>
                      </table>
                   </div>
                </div>
                  <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden mt-8">
                     <div className="px-8 py-6 border-b border-slate-50 bg-slate-50/30">
                        <h3 className="text-xs font-black text-slate-800 uppercase tracking-widest flex items-center gap-2">
                           <span className="material-symbols-outlined text-base text-blue-500">visibility</span>
                           Recorded Observations
                        </h3>
                     </div>
                     <div className="overflow-x-auto h-[60vh]">
                        <table className="w-full">
                           <thead className="bg-slate-50/50">
                            <tr>
                               <th className="px-8 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest">Date</th>
                               <th className="px-8 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest">Media</th>
                               <th className="px-8 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest">Clarity</th>
                               <th className="px-8 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest">Growth</th>
                               <th className="px-8 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest">Status</th>
                               <th className="px-8 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest">Remarks</th>
                            </tr>
                         </thead>
                         <tbody className="divide-y divide-slate-50">
                            {recordedRows.map((o, idx) => (
                              <tr 
                                key={idx} 
                                onClick={onViewObservation}
                                className="hover:bg-slate-50/30 transition-colors cursor-pointer group"
                              >
                                 <td className="px-8 py-4 text-[11px] font-bold text-slate-600 group-hover:text-blue-600 underline underline-offset-4 decoration-slate-200 group-hover:decoration-blue-400">{o.date}</td>
                                 <td className="px-8 py-4 text-[11px] font-black text-blue-600">{o.media}</td>
                                 <td className="px-8 py-4 text-[11px] font-medium text-slate-500">{o.clarity}</td>
                                 <td className="px-8 py-4 text-[11px] font-bold text-emerald-600">{o.growth}</td>
                                 <td className="px-8 py-4">
                                    <span className="px-3 py-1 bg-emerald-50 text-emerald-600 rounded-full text-[8px] font-black uppercase tracking-widest">{o.status}</span>
                                 </td>
                                 <td className="px-8 py-4 text-[11px] font-medium text-slate-400 italic">{o.remarks}</td>
                              </tr>
                            ))}
                            {recordedRows.length === 0 && (
                              <tr>
                                <td colSpan={6} className="px-8 py-6 text-center text-[11px] font-medium text-slate-400 italic">No observations recorded yet</td>
                              </tr>
                            )}
                         </tbody>
                      </table>
                   </div>
                  </div>
                </motion.div>
              )}

              {/* {activeTab === 'Timeline' && (
                <motion.div 
                  key="timeline"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm p-8 space-y-8"
                >
                   <div className="space-y-12 relative before:absolute before:left-[11px] before:top-2 before:bottom-2 before:w-0.5 before:bg-slate-100">
                      {[
                        { time: '12-Aug 10:45', user: 'Harish S', action: 'Test Initiated', comment: 'Cycle started for LOT-2024-X45' },
                        { time: '12-Aug 11:30', user: 'Harish S', action: 'Media Tubes Assigned', comment: '4 tubes scanned and matched' },
                        { time: '12-Aug 14:00', user: 'System', action: 'Incubation Started', comment: 'Incubator INC-B02 threshold met' },
                        { time: '15-Aug 09:30', user: 'P Shyam Kumar', action: 'Observation Recorded', comment: 'Day 3 check: Clear growth' }
                      ].map((item, idx) => (
                        <div key={idx} className="relative pl-12">
                           <div className="absolute left-0 top-1.5 w-6 h-6 rounded-full bg-white border-2 border-orange-500 flex items-center justify-center shadow-sm">
                              <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                           </div>
                           <div className="space-y-1">
                              <div className="flex items-center gap-3">
                                 <p className="text-[11px] font-black text-slate-800 uppercase tracking-tight">{item.action}</p>
                                 <span className="text-[9px] font-mono text-slate-400 font-bold">{item.time}</span>
                              </div>
                              <p className="text-xs font-medium text-slate-500">{item.comment}</p>
                              <div className="flex items-center gap-1.5 pt-1">
                                 <span className="material-symbols-outlined text-[10px] text-slate-500">person</span>
                                 <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{item.user}</span>
                              </div>
                           </div>
                        </div>
                      ))}
                   </div>
                </motion.div>
              )} */}

              {/* {activeTab === 'Audit' && (
                <motion.div 
                  key="audit"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden"
                >
                   <div className="overflow-x-auto">
                      <table className="w-full">
                         <thead className="bg-slate-50/50">
                          <tr>
                             <th className="px-8 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest">Date & Time</th>
                             <th className="px-8 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest">User</th>
                             <th className="px-8 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest">Action</th>
                             <th className="px-8 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest">Old Value</th>
                             <th className="px-8 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest">New Value</th>
                          </tr>
                       </thead>
                       <tbody className="divide-y divide-slate-50">
                          {[1, 2, 3].map(i => (
                            <tr key={i}>
                               <td className="px-8 py-4 text-[11px] font-bold text-slate-600">12-Aug 10:45</td>
                               <td className="px-8 py-4 text-[11px] font-black text-slate-700">Harish S</td>
                               <td className="px-8 py-4 text-[11px] font-medium text-slate-500">Status Update</td>
                               <td className="px-8 py-4 text-[11px] font-medium text-slate-400 italic">Draft</td>
                               <td className="px-8 py-4 text-[11px] font-black text-emerald-600 uppercase">In Progress</td>
                            </tr>
                          ))}
                       </tbody>
                    </table>
                   </div>
                </motion.div>
              )} */}
            </AnimatePresence>
          </div>
        </div>

        {/* Right Side Status Panel */}
        {/* <div className="col-span-12 lg:col-span-4 space-y-6">
           <div className="bg-white rounded-[2.5rem] border border-slate-100 p-8 shadow-sm space-y-8 h-full">
              <div className="flex items-center gap-3 border-b border-slate-50 pb-6">
                <span className="material-symbols-outlined text-[#0F172A]">info</span>
                <h3 className="text-xs font-black text-[#0F172A] uppercase tracking-widest">Active Status Panel</h3>
              </div>

              <div className="space-y-8">
                 <div className="space-y-2">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Current Status</p>
                    <div className="flex items-center gap-2">
                       <div className="w-2.5 h-2.5 mb-3 bg-blue-500 rounded-full animate-pulse"></div>
                       <p className="text-lg font-black text-slate-800">Incubating</p>
                    </div>
                 </div>

                 <div className="grid grid-cols-2 gap-4">
                    <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100">
                       <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Current Day</p>
                       <p className="text-lg font-black text-slate-800 mt-1">Day 3</p>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100">
                       <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Remaining</p>
                       <p className="text-lg font-black text-slate-800 mt-1">11d</p>
                    </div>
                 </div>

                 <div className="p-5 bg-orange-50 border border-orange-100 rounded-[2rem] space-y-4">
                    <div className="flex items-center gap-2 text-orange-600">
                       <span className="material-symbols-outlined text-lg mb-2 font-black">notification_important</span>
                       <p className="text-[10px] font-black uppercase tracking-widest">Next Activity</p>
                    </div>
                    <p className="text-xs font-black text-slate-800 leading-relaxed uppercase tracking-tighter">Record Observation for Day 3 is due today</p>
                    <button 
                      onClick={onRecord}
                      className="w-full py-3 bg-white text-orange-600 rounded-xl text-[10px] font-black uppercase tracking-widest border border-orange-200 hover:bg-orange-100 transition-all"
                    >
                       Proceed to Observation
                    </button>
                 </div>

                 <div className="space-y-4 pt-4 border-t border-slate-50">
                    <div className="flex items-center gap-3 text-emerald-500 opacity-60">
                       <span className="material-symbols-outlined text-lg mb-3">check_circle</span>
                       <p className="text-[10px] font-black uppercase tracking-widest">Sterility Maintained</p>
                    </div>
                    <div className="flex items-center gap-3 text-[#0F172A]">
                       <span className="material-symbols-outlined text-lg mb-3">history</span>
                       <p className="text-[10px] font-black uppercase tracking-widest">Last Obs: 12-Aug 14:00</p>
                    </div>
                 </div>
              </div>
           </div>
        </div> */}
      </div>
    </div>
  );
};

export default SterilityTestDetails;